import { useEffect } from 'react';
import { SimulatorCartItem, TimePeriod, CustomPeriodMultipliers, CustomPointValues } from '../types';
import { POINT_VALUE_METROPOLE, POINT_VALUE_DROM } from '../../../utils/pricingUtils';

const STORAGE_KEYS = {
  CART_ITEMS: 'simulator-cart-items',
  SELECTED_PERIOD: 'simulator-selected-period',
  IS_DROM: 'simulator-is-drom',
  CUSTOM_MULTIPLIERS: 'simulator-custom-multipliers',
  CUSTOM_POINT_VALUES: 'simulator-custom-point-values'
} as const;

interface UseSimulatorPersistenceProps {
  cartItems: SimulatorCartItem[];
  selectedPeriod: TimePeriod;
  isDrom: boolean;
  customMultipliers: CustomPeriodMultipliers;
  customPointValues: CustomPointValues;
}

export const useSimulatorPersistence = ({
  cartItems,
  selectedPeriod,
  isDrom,
  customMultipliers,
  customPointValues
}: UseSimulatorPersistenceProps) => {
  
  // Save cart items to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CART_ITEMS, JSON.stringify(cartItems));
    } catch (error) {
      console.warn('Failed to save cart items to localStorage:', error);
    }
  }, [cartItems]);

  // Save selected period to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SELECTED_PERIOD, selectedPeriod);
    } catch (error) {
      console.warn('Failed to save selected period to localStorage:', error);
    }
  }, [selectedPeriod]);

  // Save DROM setting to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.IS_DROM, JSON.stringify(isDrom));
    } catch (error) {
      console.warn('Failed to save DROM setting to localStorage:', error);
    }
  }, [isDrom]);

  // Save custom multipliers to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CUSTOM_MULTIPLIERS, JSON.stringify(customMultipliers));
    } catch (error) {
      console.warn('Failed to save custom multipliers to localStorage:', error);
    }
  }, [customMultipliers]);

  // Save custom point values to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CUSTOM_POINT_VALUES, JSON.stringify(customPointValues));
    } catch (error) {
      console.warn('Failed to save custom point values to localStorage:', error);
    }
  }, [customPointValues]);
};

// Helper functions to load initial state from localStorage
export const loadInitialCartItems = (): SimulatorCartItem[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.CART_ITEMS);
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    console.warn('Failed to load cart items from localStorage:', error);
    return [];
  }
};

export const loadInitialSelectedPeriod = (): TimePeriod => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.SELECTED_PERIOD);
    return (saved as TimePeriod) || 'month';
  } catch (error) {
    console.warn('Failed to load selected period from localStorage:', error);
    return 'month';
  }
};

export const loadInitialIsDrom = (): boolean => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.IS_DROM);
    return saved ? JSON.parse(saved) : false;
  } catch (error) {
    console.warn('Failed to load DROM setting from localStorage:', error);
    return false;
  }
};

export const loadInitialCustomMultipliers = (): CustomPeriodMultipliers => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.CUSTOM_MULTIPLIERS);
    return saved ? JSON.parse(saved) : {
      day: 1,
      week: 5,
      month: 20,
      year: 240
    };
  } catch (error) {
    console.warn('Failed to load custom multipliers from localStorage:', error);
    return {
      day: 1,
      week: 5,
      month: 20,
      year: 240
    };
  }
};

export const loadInitialCustomPointValues = (): CustomPointValues => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.CUSTOM_POINT_VALUES);
    return saved ? JSON.parse(saved) : {
      metropole: POINT_VALUE_METROPOLE,
      drom: POINT_VALUE_DROM
    };
  } catch (error) {
    console.warn('Failed to load custom point values from localStorage:', error);
    return {
      metropole: POINT_VALUE_METROPOLE,
      drom: POINT_VALUE_DROM
    };
  }
}